import React, { useState, useCallback, useEffect } from 'react';
import ConversationsList from '../components/conversation/ConversationsList';
import CollectionsList from '../components/collection/CollectionsList';
import CreateCollectionPopup from '../components/popups/CreateCollectionPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faComments, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { useTranslation } from '../hooks/useTranslation';
import { authFetch } from '../utils/auth';

const Conversations = () => {
  const [showCreateCollectionPopup, setShowCreateCollectionPopup] = useState(false);
  const [collections, setCollections] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [collectionConversations, setCollectionConversations] = useState([]);
  const navigate = useNavigate();
  // Keep useMsal hook for auth context without destructuring
  useMsal();
  const { t } = useTranslation();

  const fetchCollections = useCallback(async () => {
    try {
      // Use authFetch with the correct API path
      const response = await authFetch('/collections');
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      } else {
        console.error("Failed to fetch collections");
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections, refreshTrigger]);

  const handleCollectionCreated = (newCollection) => {
    setCollections(prevCollections => [...prevCollections, newCollection]);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCollectionUpdated = (updatedCollection) => {
    setCollections(prevCollections => 
      prevCollections.map(c => c._id === updatedCollection._id ? updatedCollection : c)
    );
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCollectionSelect = async (collectionId) => {
    setSelectedCollectionId(collectionId);
    try {
      // Use authFetch with the correct API path
      const response = await authFetch(`/collections/${collectionId}/conversations`);
      if (response.ok) {
        const data = await response.json();
        setCollectionConversations(data);
      } else {
        console.error('Failed to fetch collection conversations');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSeeAllConversations = () => {
    setSelectedCollectionId(null);
    setCollectionConversations([]);
  };

  const handleConversationDeleted = useCallback(async (deletedConversationId) => {
    if (selectedCollectionId) {
      setCollectionConversations(prevConversations => 
        prevConversations.filter(conv => conv._id !== deletedConversationId)
      );
    }
    
    // Update the collections state
    setCollections(prevCollections => 
      prevCollections.map(collection => ({
        ...collection,
        conversations: collection.conversations.filter(convId => convId !== deletedConversationId)
      }))
    );

    // Fetch the updated collections from the server
    await fetchCollections();
    
    setRefreshTrigger(prev => prev + 1);
  }, [selectedCollectionId, fetchCollections]);

  const handleUpdateConversation = useCallback((updatedConversation) => {
    if (selectedCollectionId) {
      setCollectionConversations(prevConversations =>
        prevConversations.map(conv => conv._id === updatedConversation._id ? updatedConversation : conv)
      );
    }
    setRefreshTrigger(prev => prev + 1);
  }, [selectedCollectionId]);

  return (
    <div className="flex flex-col md:flex-row md:space-x-4">
      {/* Main Column */}
      <div className="w-full md:w-2/3 p-4">
        <div className="flex flex-col mb-4 pb-2 border-b border-border-light">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faComments} className="mr-2 text-text-secondary" />
              <h2 className="text-lg font-semibold text-text-primary">{t('conversations.myConversations')}</h2>
            </div>
            <button
              onClick={() => navigate('/new-conversation')}
              className="bg-neutral-100 rounded text-text-secondary hover:bg-neutral-200 transition-colors flex items-center justify-center"
              style={{ borderRadius: '0.25rem', width: '1.5rem', height: '1.5rem' }}
              title={t('conversations.createNewConversation')}
            >
              <FontAwesomeIcon icon={faPlus} className="text-sm" />
            </button>
          </div>
          <p className="text-sm text-text-muted mt-1">{t('conversations.latestActivity')}</p>
        </div>
        {selectedCollectionId && (
          <div className="mb-4">
            <button
              onClick={handleSeeAllConversations}
              className="text-primary hover:text-primary-600 hover:underline flex items-center"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t('conversations.seeAllConversations')}
            </button>
          </div>
        )}
        <ConversationsList 
          onCollectionUpdated={handleCollectionUpdated}
          conversations={selectedCollectionId ? collectionConversations : null}
          onUpdateCollection={handleUpdateConversation}
          onConversationDeleted={handleConversationDeleted}
        />
      </div>

      {/* Side Column */}
      <div className="w-full md:w-1/3 p-4">
        <CollectionsList 
          refreshTrigger={refreshTrigger} 
          onCollectionSelect={handleCollectionSelect}
          onCollectionUpdated={handleCollectionUpdated}
          onCreateCollection={() => setShowCreateCollectionPopup(true)}
          onUpdateCollection={handleCollectionUpdated}
          createCollectionTooltip={t('conversations.createNewCollection')}
          collections={collections}
        />
      </div>

      {showCreateCollectionPopup && (
        <CreateCollectionPopup 
          onClose={() => setShowCreateCollectionPopup(false)}
          onCollectionCreated={handleCollectionCreated}
        />
      )}
    </div>
  );
};

export default Conversations;
